import MapLibreGlDirections, {
  type MapLibreGlDirectionsConfiguration,
  type Route,
  type Feature,
  type Point,
  type LineString,
  utils,
} from "@maplibre/maplibre-gl-directions";
import type maplibregl from "maplibre-gl";

export default class DistanceMeasurementMapLibreGlDirections extends MapLibreGlDirections {
  constructor(
    map: maplibregl.Map,
    configuration?: Partial<MapLibreGlDirectionsConfiguration>,
  ) {
    super(map, configuration);
  }

  // here we save the original method to be able to use it in the re-defined one. For some methods (namely those
  // that are defined as methods and not as properties) you can instead call their "super" counterparts, but for the
  // methods as `buildRoutelines` it's impossible due to restrictions implied by the language itself, so that's the
  // only reasonable way to be able to use the original functionality as a part of the re-defined method
  originalBuildRoutelines = utils.buildRoutelines;

  // re-defining the original `buildRoutelines` method
  protected buildRoutelines = (
    requestOptions: MapLibreGlDirectionsConfiguration["requestOptions"],
    routes: Route[],
    selectedRouteIndex: number,
    snappoints: Feature<Point>[],
  ): Feature<LineString>[][] => {
    // first we call the original method. It returns the built routelines
    const routelines = this.originalBuildRoutelines(
      requestOptions,
      routes,
      selectedRouteIndex,
      snappoints,
    );

    // then we modify the routelines adding to each route leg a property that stores the leg's distance
    routelines[0].forEach((leg, index) => {
      if (leg.properties)
        leg.properties.distance = Number(
          ((routes[0].legs[index].distance as number) / 1000).toFixed(1),
        );
    });

    // and returning the modified routelines
    return routelines;
  };
}

// using a different source name. That might become useful if you'd like to use the Distance Measurement Directions
// instance along with a normal Directions instance on the same map
const sourceName = "distance-measurement-maplibre-gl-directions";

const config: Partial<MapLibreGlDirectionsConfiguration> = {
  api: "https://api.mapbox.com/directions/v5",
  profile: "mapbox/driving-traffic",
  makePostRequest: true,
  requestOptions: {
    access_token:
      "pk.eyJ1IjoiZGVuaXNybCIsImEiOiJjbTluZWpxeGkwb2ZyMm1xZWs0OW00NnN2In0.fKtC9m99B2uAFzYguIjbfQ",
    alternatives: "true",
    overview: "full",
  },
  sourceName,
  layers: [
    {
      id: `${sourceName}-snapline`,
      type: "line",
      source: sourceName,
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
      paint: {
        "line-dasharray": [2, 2],
        "line-color": "#aaaaaa",
        "line-width": 2,
      },
      filter: ["==", ["get", "type"], "SNAPLINE"],
    },
    {
      id: `${sourceName}-routeline-casing`,
      type: 'line',
      source: sourceName,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#e5e7eb',
        'line-opacity': 0.8,
        'line-width': 9,
      },
      filter: ['==', ['get', 'route'], 'SELECTED'],
    },
    {
      id: `${sourceName}-routeline`,
      type: "line",
      source: sourceName,
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
      paint: {
        'line-color': '#1d4ed8',
        "line-opacity": 0.9,
        "line-width": 5,
      },
      filter: ["==", ["get", "route"], "SELECTED"],
    },
    {
      id: `${sourceName}-routeline-distance`,
      type: "symbol",
      source: sourceName,
      layout: {
        "symbol-placement": "line-center",
        "text-field": "{distance}km",
        "text-size": 14,
        "text-ignore-placement": true,
        "text-allow-overlap": true,
        "text-overlap": "always",
        "text-font": ["Noto Sans Regular"],
        'text-offset': [0, -1.5],
      },
      paint: {
        "text-color": "#1f2937",
        "text-halo-color": "rgba(255, 255, 255, 0.8)",
        "text-halo-width": 1.5,
        "text-halo-blur": 0.5,
      },
      filter: ["==", ["get", "route"], "SELECTED"],
    },
    {
      id: `${sourceName}-hoverpoint`,
      type: "circle",
      source: sourceName,
      paint: {
        "circle-radius": 9,
        "circle-color": "#212121",
      },
      filter: ["==", ["get", "type"], "HOVERPOINT"],
    },
    {
      id: `${sourceName}-snappoint`,
      type: "circle",
      source: sourceName,
      paint: {
        "circle-radius": 7,
        "circle-color": "#fb923c",
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.5,
      },
      filter: ["==", ["get", "type"], "SNAPPOINT"],
    },
    {
      id: `${sourceName}-waypoint`,
      type: "circle",
      source: sourceName,
      paint: {
        "circle-radius": 4,
        "circle-color": "#ffffff",
        'circle-stroke-color': '#1d4ed8',
        'circle-stroke-width': 3,
      },
      filter: ["==", ["get", "type"], "WAYPOINT"],
    },
  ] as maplibregl.LayerSpecification[],
  // don't forget to update the sensitive layers
  sensitiveSnappointLayers: [`${sourceName}-snappoint`],
  sensitiveWaypointLayers: [`${sourceName}-waypoint`],
  sensitiveRoutelineLayers: [`${sourceName}-routeline-casing`, `${sourceName}-routeline`],
  sensitiveAltRoutelineLayers: [],
};

export { config };
